// -----------------------------------------------------------------------------
// AttendedGuidedChecklist  (Phase 3.5 — hero gate integration + spawn refresh)
//
// Orchestrates the guided-checklist experience inside the Attended popup.
// Reused by both:
//   - OpenAttendedPopup  (via AttendedAppointment, mode="gate-first")
//   - OpenConsultationPopup  (via ConsultationAttendedAppointment, mode="post-attended")
//
// Phase history:
//   Phase 1 — shell + hook + feature-flag gating
//   Phase 2 — AttendanceGate (Yes/No)
//   Phase 3 — UpNextCard + ChecklistList + progress strip + animations
//   Phase 3.5 — hero gate integration + spawn refresh + CRM button style  ← here
//   Phase 4+ — ActionPanel + individual action panels
//
// Gate rendering modes:
//   gateRenderedInHost=false (default): orchestrator renders AttendanceGate internally.
//   gateRenderedInHost=true: parent host (AttendedAppointment) renders the gate
//     inside its hero card; orchestrator skips its own gate render and watches
//     externalGateDismissed instead.
//
// Spawn refresh:
//   After gate dismisses, the BE spawns task-checklist rows. We wait 600 ms then
//   call refresh(). If tasks are still absent after the fetch, we retry once more
//   after 1000 ms (cap: 2 retries). While waiting, a "Spawning your tasks…"
//   spinner replaces the body so the user never sees an empty-state flash.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button, useMediaQuery } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch } from 'src/redux/store';
import { completeChecklistItem } from 'src/redux/slices/taskSlice';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import cascadeTaskDueDates from 'src/utils/cascadeTaskDueDates';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { FollowUp } from '../../../../components/tabs';
import useGuidedChecklist from './hooks/useGuidedChecklist';
import useUndoAttendance from './hooks/useUndoAttendance';
import AttendanceGate from './AttendanceGate';
import UndoBanner from './UndoBanner';
import UpNextCard from './UpNextCard';
import RecentlyCompletedCard from './RecentlyCompletedCard';
import ChecklistMiniGrid from './ChecklistMiniGrid';
import ActionPanel from './ActionPanel';
import TasksEmptyStateCard from './TasksEmptyStateCard';
import { INLINE_HANDLED_TYPES } from './panels';
import FollowUpPanel from './panels/FollowUpPanel';
import { COMPLETION_LABELS, DEFAULT_COMPLETION_LABEL } from './actionMeta';
import BulkAssignModal from './BulkAssignModal';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const BOOKED_STATUS = 'CONSULTATION_BOOKED';
const SPAWN_INITIAL_DELAY_MS = 600;
const SPAWN_RETRY_DELAY_MS = 1000;
const SPAWN_MAX_RETRIES = 2;

// ---------------------------------------------------------------------------

// Spawn-failure fallback: tasks couldn't be created, so we drop to a manual
// FollowUp. That FollowUp doesn't report completion back to the host, so the
// host's outcome gate (activeTab) would stay empty and trap the user on Finish
// ("Please select any one Tab"). Firing onTaskActivity on mount auto-selects
// the "Reminder & Follow-up" outcome — reaching this branch IS that path.
function SpawnFailureFallback({
    handleClose,
    onTaskActivity,
    practiceId,
    leadId,
    leadDetail,
    handleReloadLeads,
}) {
    useEffect(() => {
        if (onTaskActivity) onTaskActivity();
    }, [onTaskActivity]);
    const handleRecorded = () => {
        // Contact already logged by the sub-panel; just mark host activity.
        // Don't close — the follow-up scheduler stays available below.
        if (onTaskActivity) onTaskActivity();
    };
    return (
        <Box data-testid="spawn-failure-fallback" sx={{ py: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', mb: 0.25, letterSpacing: '-0.01em' }}>
                No checklist for this lead
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6e6e73', mb: 1, letterSpacing: '-0.005em' }}>
                Record what you did to contact the patient, then set a follow-up below.
            </Typography>
            <FollowUpPanel
                item={null}
                practiceId={practiceId}
                leadId={leadId}
                leadDetail={leadDetail}
                onClose={handleClose}
                onComplete={handleRecorded}
                handleReloadLeads={handleReloadLeads}
            />
            <Box sx={{ borderTop: '1px solid rgba(145,158,171,0.16)', mt: 1.5, pt: 1.5 }}>
                <FollowUp type="lead" handleClose={handleClose} />
            </Box>
        </Box>
    );
}
SpawnFailureFallback.propTypes = {
    handleClose: PropTypes.func,
    onTaskActivity: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    handleReloadLeads: PropTypes.func,
};

export default function AttendedGuidedChecklist({
    practiceId,
    leadId,
    leadDetail,
    mode,
    handleClose,
    handleNoAttended,
    gateRenderedInHost,
    externalGateDismissed,
    handleReloadLeads,
    onTaskActivity,
    onCreateReminder,
}) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    // Internal gate state — skip the gate if the lead is already CONSULTATION_ATTENDED
    // (handles popup re-open on the same lead without re-prompting attendance).
    const initiallyAttended = (leadDetail?.lead_treatments || []).some(
        (t) => t.status === 'CONSULTATION_ATTENDED'
    );
    const [gateDismissed, setGateDismissed] = useState(initiallyAttended);
    // Undo-attendance banner — only active in gate-first mode.
    const { showBanner, secondsLeft, startUndo, undo, dismiss: dismissUndo } = useUndoAttendance();
    // Ref to the "Take action" CTA button — used to return focus when panel closes.
    const actionBtnRef = useRef(null);
    // Visually-hidden SR announcement text — updated after panel success/complete.
    const [srAnnouncement, setSrAnnouncement] = useState('');
    // Announce a transient message via the SR-only live region, then clear it.
    const announce = useCallback((msg) => {
        setSrAnnouncement(msg);
        setTimeout(() => setSrAnnouncement(''), 2500);
    }, []);
    // Promote a list item to Up Next when user clicks a Today/Later row.
    const [featuredOverride, setFeaturedOverride] = useState(null);
    // Action panel — set when user taps a CTA; null = panel closed.
    const [actionPanelItem, setActionPanelItem] = useState(null);
    // Stagger animation trigger — set true once the guided body becomes visible.
    const [bodyMounted, setBodyMounted] = useState(false);
    // True while we're waiting for BE to spawn tasks after the gate dismisses.
    const [spawnLoading, setSpawnLoading] = useState(false);
    // True when all automatic retries are exhausted — shows a manual Retry button.
    const [spawnGaveUp, setSpawnGaveUp] = useState(false);
    const spawnRetryRef = useRef(0);
    // Optimistic due-date overrides: Map<taskId, Date> applied before rendering.
    // Reverted on PATCH failure.
    const [dueDateOverrides, setDueDateOverrides] = useState(() => new Map());
    // Post-action transition state: set briefly after completing an action.
    // Shape: { completed: item, incoming: item|null, label: string, completedAt: Date }
    // While set, the card area renders a 30/70 swap layout (confirmation + new featured task).
    const [recentlyCompleted, setRecentlyCompleted] = useState(null);
    // Bulk-assign modal open state.
    const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
    const recentlyCompletedTimerRef = useRef(null);
    // When the gate is owned internally AND the lead is already CONSULTATION_ATTENDED
    // at mount, skip the spawn-refresh sequence — tasks were created in a prior session.
    // When gateRenderedInHost=true the host drives dismissal, so never skip.
    const skipSpawnRef = useRef(!gateRenderedInHost && initiallyAttended);

    const { loading, templatesEmpty, groups, upNext, templatePreviewItems = [], allDoneCount, refresh } = useGuidedChecklist({
        practiceId,
        leadId,
        leadDetail,
    });

    // Flatten groups early so effects can use totalCount.
    const { today = [], later = [], done = [] } = groups || {};
    const totalCount = today.length + later.length + done.length;
    // All non-done active tasks — used for bulk-assign.
    const activeTasks = [...today, ...later].filter((t) => t.taskId);
    const doneCount = done.length;
    // allDone via two paths:
    //   1. BE returns completed items in checklist_items → normal count check.
    //   2. BE omits completed items (server-side filter) → allDoneCount > 0 from
    //      progress-based detection in the hook.
    const allDone = (totalCount > 0 && doneCount === totalCount) || allDoneCount > 0;

    // The effective gate-dismissed flag: from parent prop when hosted, else internal.
    const effectiveGateDismissed = gateRenderedInHost ? !!externalGateDismissed : gateDismissed;

    // Guided body is visible when: not in initial load, templates exist, not spawn-
    // loading, and the gate has been dismissed (or we're in post-attended mode).
    const showBody =
        !loading &&
        !templatesEmpty &&
        !spawnLoading &&
        (mode !== 'gate-first' || effectiveGateDismissed);

    // ---------------------------------------------------------------------------
    // Stagger mount animation trigger
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!showBody) {
            setBodyMounted(false);
            return undefined;
        }
        const t = setTimeout(() => setBodyMounted(true), 16);
        return () => clearTimeout(t);
    }, [showBody]);

    // ---------------------------------------------------------------------------
    // Spawn-refresh sequence: triggered when gate is dismissed.
    //   1. Set spawnLoading=true immediately (shows "Spawning…" spinner).
    //   2. After SPAWN_INITIAL_DELAY_MS, call refresh().
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!effectiveGateDismissed) {
            spawnRetryRef.current = 0;
            return undefined;
        }
        // Lead was already CONSULTATION_ATTENDED at mount — tasks exist, no spawn needed.
        if (skipSpawnRef.current) return undefined;
        setSpawnLoading(true);
        spawnRetryRef.current = 0;
        const t = setTimeout(() => refresh(), SPAWN_INITIAL_DELAY_MS);
        return () => clearTimeout(t);
    }, [effectiveGateDismissed]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------------------------------------------------------------------
    // Retry if tasks are still absent after a completed refresh.
    // Runs whenever loading transitions false while spawnLoading is active.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!spawnLoading || loading) return undefined;
        // Exit spawn-loading when tasks are present (either returned as items, or
        // detected as all-done via group.progress by the hook).
        if (totalCount > 0 || allDoneCount > 0) {
            setSpawnLoading(false);
            return undefined;
        }
        if (spawnRetryRef.current >= SPAWN_MAX_RETRIES) {
            setSpawnGaveUp(true);
            // If preview items are available, exit spawn-loading so the body renders
            // via the isPreview path instead of showing the V1 FollowUp fallback.
            if (templatePreviewItems.length > 0) {
                setSpawnLoading(false);
            }
            return undefined;
        }
        spawnRetryRef.current += 1;
        const t = setTimeout(() => refresh(), SPAWN_RETRY_DELAY_MS);
        return () => clearTimeout(t);
    }, [spawnLoading, loading, totalCount, allDoneCount, templatePreviewItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Esc key — close action panel if open.
    useEffect(() => {
        if (!actionPanelItem) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setActionPanelItem(null);
                setTimeout(() => actionBtnRef.current?.focus(), 32);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [actionPanelItem]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------------------------------------------------------------------
    // Early returns (all hooks above this line)
    // ---------------------------------------------------------------------------

    if (!hasFeatureAccess('tasks-v2', practiceId)) return null;

    // Initial load spinner (distinct from spawn-loading spinner below).
    if (loading && !spawnLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#078DEE' }} />
            </Box>
        );
    }

    if (templatesEmpty) return null;

    // ---------------------------------------------------------------------------
    // Gate (when rendered internally, i.e. gateRenderedInHost=false)
    // ---------------------------------------------------------------------------
    const isLeadBooked = (leadDetail?.lead_treatments || []).some(
        (t) => t.status === BOOKED_STATUS
    );

    if (!gateRenderedInHost && mode === 'gate-first' && isLeadBooked && !gateDismissed) {
        const handleGateDismissed = () => {
            setGateDismissed(true);
            // Start undo timer — use first BOOKED treatment id (or any treatment id as fallback)
            const ltId =
                (leadDetail?.lead_treatments || []).find(
                    (t) => t.status === 'CONSULTATION_BOOKED'
                )?.id || leadDetail?.lead_treatments?.[0]?.id;
            if (ltId) startUndo(ltId);
        };
        return (
            <AttendanceGate
                variant="card"
                leadDetail={leadDetail}
                handleNoAttended={handleNoAttended}
                onDismissed={handleGateDismissed}
            />
        );
    }

    // ---------------------------------------------------------------------------
    // Spawn-loading state — "Spawning your tasks…"
    // ---------------------------------------------------------------------------
    if (spawnLoading) {
        // All retries exhausted + no tasks spawned → fall back to V1 FollowUp
        // so the user can set a manual reminder without being permanently stuck.
        // Only fall back to V1 FollowUp when there are no preview items either —
        // if a template applies, show the preview body instead.
        if (spawnGaveUp && totalCount === 0 && templatePreviewItems.length === 0) {
            return (
                <SpawnFailureFallback
                    handleClose={handleClose}
                    onTaskActivity={onTaskActivity}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadDetail}
                    handleReloadLeads={handleReloadLeads}
                />
            );
        }
        const handleManualRetry = () => {
            setSpawnGaveUp(false);
            spawnRetryRef.current = 0;
            refresh();
        };
        // After at least one retry, give the user a more informative message.
        const spawnMessage = spawnRetryRef.current >= 1
            ? 'Tasks are taking longer than usual…'
            : 'Spawning your tasks…';
        return (
            <Box
                data-testid="spawn-loading"
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, px: 1 }}
            >
                {!spawnGaveUp && <CircularProgress size={18} sx={{ color: '#078DEE', flexShrink: 0 }} />}
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                    {spawnMessage}
                </Typography>
                {spawnGaveUp && (
                    <Button
                        data-testid="spawn-retry-btn"
                        size="small"
                        onClick={handleManualRetry}
                        sx={{ fontSize: '12px', color: '#078DEE', textTransform: 'none', p: 0, minWidth: 0 }}
                    >
                        Retry
                    </Button>
                )}
            </Box>
        );
    }

    // Suppress body in gate-first mode until gate is dismissed (prevents empty
    // state flash before spawn-refresh sequence begins).
    if (!showBody) return null;

    // ---------------------------------------------------------------------------
    // Stagger helper — defined here so preview mode can also use it.
    // ---------------------------------------------------------------------------
    const fadeSx = (delayMs) =>
        reducedMotion
            ? {}
            : {
                opacity: bodyMounted ? 1 : 0,
                transform: bodyMounted ? 'none' : 'translateY(20px)',
                transition: `opacity 480ms ${EASING} ${delayMs}ms, transform 480ms ${EASING} ${delayMs}ms`,
            };

    // ---------------------------------------------------------------------------
    // All-done — celebratory card.
    // onCreateReminder: when provided shows the reminder CTA; falls back to Close.
    // ---------------------------------------------------------------------------
    if (allDone) {
        return (
            <Box data-testid="attended-guided-checklist" data-mode={mode} sx={{ py: 2 }}>
                <TasksEmptyStateCard
                    variant="all-complete"
                    onCreateReminder={onCreateReminder || null}
                    onClose={handleClose}
                />
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // Empty items (no live tasks, no preview templates)
    // ---------------------------------------------------------------------------
    if (totalCount === 0 && templatePreviewItems.length === 0) {
        return (
            <Box data-testid="attended-guided-checklist" data-mode={mode} sx={{ py: 2 }}>
                <TasksEmptyStateCard
                    variant="no-tasks"
                    onCreateReminder={onCreateReminder || null}
                />
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // Due-date override helpers — defined here (before effectiveUpNext usage)
    // ---------------------------------------------------------------------------

    /** Apply any pending optimistic due_at override to an item before rendering. */
    const applyDueDateOverride = (item) => {
        if (!item || !dueDateOverrides.has(item.taskId)) return item;
        return { ...item, due_at: dueDateOverrides.get(item.taskId).toISOString() };
    };

    /**
     * Change the due date of item (and cascade-shift other incomplete tasks).
     * Applies optimistic overrides immediately; reverts on any PATCH failure.
     *
     * @param {object} item     - The checklist item the user edited.
     * @param {Date}   newDate  - The new due date (JS Date from picker).
     */
    const handleChangeDueDate = async (item, newDate) => {
        // Preview items have no taskId — spawn from template first to get a real taskId.
        if (!item?.taskId) {
            if (!item?._templateId) {
                dispatch(updateError({ success: false, message: "Due dates can't be set on this item yet." }));
                return;
            }
            const spawnResp = await callApi(
                `/api/v1/practices/${practiceId}/tasks/from-template/`,
                'post',
                { template_id: item._templateId, lead_id: leadId }
            );
            if (!spawnResp.ok && spawnResp.status !== 200 && spawnResp.status !== 201) {
                dispatch(updateError({ success: false, message: "Couldn't create tasks. Please try again." }));
                return;
            }
            const newTaskId = spawnResp.data?.id;
            if (newTaskId) {
                await callApi(`/api/v1/practices/${practiceId}/tasks/${newTaskId}/`, 'patch', { due_at: newDate.toISOString() });
                dispatch(updateError({ success: true, message: 'Tasks created and due date set.' }));
            } else {
                dispatch(updateError({ success: true, message: 'Tasks created! Select a task to set its due date.' }));
            }
            refresh();
            return;
        }
        const allItems = [...today, ...later, ...done];
        const cascade = cascadeTaskDueDates({
            items: allItems,
            anchorTaskId: item.taskId,
            oldDueAt: item.due_at,
            newDueAt: newDate,
        });

        // Optimistic update.
        const prevOverrides = new Map(dueDateOverrides);
        const nextOverrides = new Map(dueDateOverrides);
        cascade.forEach(({ taskId, newDueAt: d }) => nextOverrides.set(taskId, d));
        setDueDateOverrides(nextOverrides);

        // PATCH all affected tasks in parallel.
        const results = await Promise.allSettled(
            cascade.map(({ taskId, newDueAt: d }) =>
                callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', {
                    due_at: d.toISOString(),
                    ...(taskId === item.taskId ? { is_anchor: true } : {}),
                })
            )
        );

        const anyFailed = results.some((r) => r.status === 'rejected' || !r.value?.ok);
        if (anyFailed) {
            // Revert optimistic state and surface error.
            setDueDateOverrides(prevOverrides);
            dispatch(updateError({ success: false, message: "Couldn't update due dates. Please try again." }));
            return;
        }

        // Success toast.
        const nShifted = cascade.length;
        const oldMs = item.due_at ? (parseApiDate(item.due_at)?.valueOf() ?? Date.now()) : Date.now();  // COMPARE
        const deltaDays = Math.round(Math.abs(newDate.getTime() - oldMs) / (24 * 60 * 60 * 1000));
        dispatch(updateError({
            success: true,
            message: `Due dates updated — ${nShifted} task${nShifted !== 1 ? 's' : ''} shifted by ${deltaDays} day${deltaDays !== 1 ? 's' : ''}`,
        }));
        // Rescheduling is a completed action for this visit — fire so the host
        // sets the attended outcome and the Finish button activates (same as
        // complete/skip/pause).
        if (onTaskActivity) onTaskActivity();
        refresh();
    };

    // ---------------------------------------------------------------------------
    // Active data — when no live tasks exist but templates do, treat preview items
    // identically to real spawned tasks (no preview-banner, fully interactive).
    // ---------------------------------------------------------------------------
    const isPreview = totalCount === 0 && templatePreviewItems.length > 0;
    const activeToday  = isPreview ? templatePreviewItems : today;
    const activeLater  = isPreview ? [] : later;
    const activeDone   = isPreview ? [] : done;
    const activeDoneCount  = activeDone.length;
    const activeTotalCount = activeToday.length + activeLater.length + activeDoneCount;

    const effectiveUpNext = applyDueDateOverride(
        featuredOverride || (isPreview ? (templatePreviewItems[0] || null) : upNext)
    );
    // Compute the "Next up" preview item — the one that would become Up Next after
    // the current is completed/skipped. Dequeue from the active pool by finding
    // effectiveUpNext then taking the next index.
    const activePool = [...activeToday, ...activeLater].filter((i) => !i.completed_at);
    const currentIdx = effectiveUpNext?.id != null
        ? activePool.findIndex((i) => i.id === effectiveUpNext.id)
        : 0;
    // When currentIdx is -1 (featured item was completed and fell out of activePool),
    // fall back to the first two remaining items so the right pane can still show something.
    const nextUpNextRaw = currentIdx >= 0
        ? (activePool[currentIdx + 1] || null)
        : (activePool[1] || activePool[0] || null);
    const nextUpNext = nextUpNextRaw ? applyDueDateOverride(nextUpNextRaw) : null;
    // When the current featured item IS the last item in the pool, signal the right
    // pane to show an "All caught up" placeholder instead of going completely blank.
    const isLastInPool = currentIdx >= 0 && currentIdx === activePool.length - 1;
    const nextEmptyReason = (!nextUpNext && isLastInPool) ? 'caught_up' : null;

    // Apply overrides to carousel groups so due-date labels update immediately.
    const overriddenActiveGroups = {
        today: activeToday.map(applyDueDateOverride),
        later: activeLater.map(applyDueDateOverride),
        done:  activeDone.map(applyDueDateOverride),
    };

    // Stats strip helpers
    const hasOverdue = activeToday.some((item) => item.due_at && (parseApiDate(item.due_at)?.valueOf() ?? Infinity) < Date.now());  // COMPARE
    const todayChipColor = hasOverdue ? '#FF5630' : '#FFAB00';

    // ---------------------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------------------
    const handleItemClick = (item) => {
        if (item.completed_at) return;
        setFeaturedOverride(item);
    };

    const handleSkip = (skippedItem) => {
        const pool = isPreview ? templatePreviewItems : [...today, ...later];
        const remaining = pool.filter(
            (i) => !i.completed_at && i.id !== skippedItem.id
        );
        // Preview items have no real taskId/itemId — skip client-side only.
        if (!skippedItem?.taskId || !skippedItem?.itemId) {
            setFeaturedOverride(remaining[0] || null);
            return;
        }
        // Persist the skip to the BE so the item stays de-prioritised after reload.
        // Uses the same /complete/ endpoint as a full completion, with SKIPPED state.
        // Optimistic UI advance is gated on a confirmed 200 — same pattern as handlePanelComplete.
        dispatch(completeChecklistItem(
            practiceId, skippedItem.taskId, skippedItem.itemId,
            ({ success }) => {
                if (success) {
                    setFeaturedOverride(remaining[0] || null);
                    if (onTaskActivity) onTaskActivity();
                    refresh();
                }
                // On failure the thunk already surfaced an error toast; leave the card as-is.
            },
            { source: 'upnext_skip' },
            CHECKLIST_COMPLETION_STATE.SKIPPED,
        ));
    };

    const handleActionClick = (item) => {
        if (!item) return;
        // Inline-handled types (YES_NO_TOGGLE, CONFIRMATION, LINKED_CHECKLIST) don't open a panel
        if (INLINE_HANDLED_TYPES.has(item?.item_type)) return;
        setActionPanelItem(item);
    };

    const handlePanelClose = () => {
        setActionPanelItem(null);
        // Return focus to the CTA button that opened the panel.
        setTimeout(() => actionBtnRef.current?.focus(), 32);
    };

    const handlePanelSendSuccess = () => {
        setActionPanelItem(null);
        announce('Email sent and marked complete.');
        setTimeout(() => actionBtnRef.current?.focus(), 32);
        if (actionPanelItem) {
            // Snapshot nextUpNext before refresh() advances the pool.
            const label = COMPLETION_LABELS[actionPanelItem.item_type] || DEFAULT_COMPLETION_LABEL;
            if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
            setRecentlyCompleted({ completed: actionPanelItem, incoming: nextUpNext, label, completedAt: new Date() });
            recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
            if (onTaskActivity) onTaskActivity();
        }
        refresh();
    };

    const applyPanelCompleteSuccess = (completedItem) => {
        const label = COMPLETION_LABELS[completedItem.item_type] || DEFAULT_COMPLETION_LABEL;
        if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
        setRecentlyCompleted({ completed: completedItem, incoming: nextUpNext, label, completedAt: new Date() });
        recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
        if (onTaskActivity) onTaskActivity();
    };

    // Called by non-email panels after successful action submission.
    // Marks the checklist item complete on the backend, then refreshes.
    const handlePanelComplete = (completedItem) => {
        setActionPanelItem(null);
        announce('Task marked complete.');
        setTimeout(() => actionBtnRef.current?.focus(), 32);
        if (!completedItem?.taskId || !completedItem?.itemId) { refresh(); return; }
        if (completedItem.completionAlreadyPersisted) {
            applyPanelCompleteSuccess(completedItem);
            refresh();
            return;
        }
        // For LEAD_NEXT_ACTION the BE requires resolved_action in the body.
        // NextActionPanel attaches resolvedAction to the completedItem before
        // calling onComplete — forward it here so the POST includes the field.
        // The guided multi-channel follow-up sequence (FollowUpPanel) additionally
        // attaches channelOutcomes — persist it as response_payload.channels.
        let payload = completedItem.resolvedAction
            ? { resolved_action: completedItem.resolvedAction }
            : null;
        if (Array.isArray(completedItem.channelOutcomes) && completedItem.channelOutcomes.length > 0) {
            payload = { ...(payload || {}), channels: completedItem.channelOutcomes };
        }
        dispatch(completeChecklistItem(
            practiceId, completedItem.taskId, completedItem.itemId,
            ({ success }) => {
                // Gate the green confirmation card behind a confirmed 200 from the BE.
                // Previously setRecentlyCompleted fired optimistically before the thunk,
                // so a 400/500 response still showed "Email sent" then silently reverted.
                if (success) {
                    applyPanelCompleteSuccess(completedItem);
                }
                refresh();
            },
            payload,
        ));
    };

    // ---------------------------------------------------------------------------
    // Guided checklist body
    // ---------------------------------------------------------------------------
    return (
        <Box data-testid="attended-guided-checklist" data-mode={mode} sx={{ py: 2 }}>
            {/* Screen-reader-only live region for transient announcements */}
            <Box
                aria-live="polite"
                aria-atomic="true"
                sx={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    whiteSpace: 'nowrap',
                }}
            >
                {srAnnouncement}
            </Box>
            {/* Undo-attendance banner (gate-first only, 30s window) */}
            {showBanner && mode === 'gate-first' && (
                <UndoBanner
                    secondsLeft={secondsLeft}
                    onUndo={undo}
                    onDismiss={dismissUndo}
                />
            )}
            {/* Split row: checklist left (43% when panel open, 100% otherwise) + action panel right (57%) */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Left column — checklist content */}
                <Box
                    sx={{
                        flex: actionPanelItem ? '0 0 43%' : '1',
                        minWidth: 0,
                        transition: `flex 320ms ${EASING}`,
                    }}
                >
                    {/* Stats strip — chips left, progress bar right */}
                    <Box
                        data-testid="stats-strip"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            minHeight: 28,
                            mb: 2,
                            ...fadeSx(0),
                        }}
                    >
                        {/* Stat chips — hidden when count is 0 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {activeToday.length > 0 && (
                                <Box data-testid="stats-chip-today" aria-label={`${activeToday.length} ${activeToday.length === 1 ? 'task' : 'tasks'} due today`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: todayChipColor, flexShrink: 0 }} />
                                    <Typography aria-hidden="true" sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                        {activeToday.length} Due today
                                    </Typography>
                                </Box>
                            )}
                            {activeLater.length > 0 && (
                                <Box data-testid="stats-chip-later" aria-label={`${activeLater.length} ${activeLater.length === 1 ? 'task' : 'tasks'} due later`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6e6e73', flexShrink: 0 }} />
                                    <Typography aria-hidden="true" sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                        {activeLater.length} Later
                                    </Typography>
                                </Box>
                            )}
                            {activeDoneCount > 0 && (
                                <Box data-testid="stats-chip-done" aria-label={`${activeDoneCount} ${activeDoneCount === 1 ? 'task' : 'tasks'} done`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34c759', flexShrink: 0 }} />
                                    <Typography aria-hidden="true" sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                        {activeDoneCount} Done
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Assign all button — visible when there are active tasks */}
                        {activeTasks.length > 0 && (
                            <Button
                                data-testid="bulk-assign-btn"
                                size="small"
                                variant="outlined"
                                startIcon={<Iconify icon="mdi:account-multiple-plus-outline" width={14} height={14} />}
                                onClick={() => setBulkAssignOpen(true)}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    color: '#078DEE',
                                    borderColor: '#078DEE44',
                                    py: 0.5,
                                    px: 1.25,
                                    '&:hover': { bgcolor: '#078DEE0A', borderColor: '#078DEE' },
                                }}
                            >
                                Assign all
                            </Button>
                        )}

                        {/* Progress bar */}
                        <Box sx={{ width: 200, height: 4, borderRadius: '2px', bgcolor: 'rgba(0,0,0,0.06)', overflow: 'hidden', flexShrink: 0 }}>
                            <Box
                                data-testid="progress-bar-fill"
                                sx={{
                                    height: '100%',
                                    width: `${activeTotalCount > 0 ? (activeDoneCount / activeTotalCount) * 100 : 0}%`,
                                    bgcolor: '#34c759',
                                    borderRadius: '2px',
                                    transition: 'width 400ms ease',
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Up Next card — normal layout or post-action 30/70 swap */}
                    <Box sx={{ mb: 2, ...fadeSx(80) }}>
                        {recentlyCompleted ? (
                            /* Post-action transition: LEFT 30% confirmation + RIGHT 70% new featured task */
                            <Box
                                data-testid="upnext-swap-layout"
                                sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}
                            >
                                <Box sx={{ flex: '0 0 calc(30% - 8px)', minWidth: 0 }}>
                                    <RecentlyCompletedCard
                                        item={recentlyCompleted.completed}
                                        label={recentlyCompleted.label}
                                        completedAt={recentlyCompleted.completedAt}
                                        onDismiss={() => setRecentlyCompleted(null)}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <UpNextCard
                                        key={recentlyCompleted.incoming?.id ?? 'incoming'}
                                        item={recentlyCompleted.incoming || effectiveUpNext}
                                        leadEmail={leadDetail?.email || leadDetail?.lead_contact?.email || ''}
                                        onActionClick={handleActionClick}
                                        onSkip={handleSkip}
                                        actionBtnRef={actionBtnRef}
                                        onChangeDueDate={handleChangeDueDate}
                                        nextItem={null}
                                        nextEmptyReason={null}
                                        onItemClick={handleItemClick}
                                        practiceId={practiceId}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <UpNextCard
                                key={effectiveUpNext?.id ?? 'none'}
                                item={effectiveUpNext}
                                leadEmail={leadDetail?.email || leadDetail?.lead_contact?.email || ''}
                                onActionClick={handleActionClick}
                                onSkip={handleSkip}
                                actionBtnRef={actionBtnRef}
                                onChangeDueDate={handleChangeDueDate}
                                nextItem={nextUpNext}
                                nextEmptyReason={nextEmptyReason}
                                onItemClick={handleItemClick}
                                practiceId={practiceId}
                            />
                        )}
                    </Box>

                    {/* Compact horizontal mini-grid — 2 cards + "+N more" expand */}
                    <Box sx={{ ...fadeSx(160) }}>
                        <ChecklistMiniGrid
                            groups={overriddenActiveGroups}
                            featuredItem={effectiveUpNext}
                            onItemClick={handleItemClick}
                        />
                    </Box>
                </Box>

                {/* Right column — action panel (rendered when actionPanelItem set) */}
                {actionPanelItem && (
                    <ActionPanel
                        // Keyed by item id (same convention as UpNextCard above) so a
                        // switch to a different action item forces a full remount —
                        // otherwise ActionPanel's mount-only EMAIL_SENT/WHATSAPP_SENT
                        // template-resolve effects (and FollowUpPanel's own mount
                        // effect + local state) never re-run for the new item, and an
                        // in-flight resolve for the PREVIOUS item can land and apply
                        // the wrong template to the sidebar composer.
                        key={actionPanelItem?.id ?? 'none'}
                        item={actionPanelItem}
                        practiceId={practiceId}
                        leadId={leadId}
                        leadDetail={leadDetail}
                        onClose={handlePanelClose}
                        onSendSuccess={handlePanelSendSuccess}
                        onComplete={handlePanelComplete}
                        handleReloadLeads={handleReloadLeads}
                    />
                )}
            </Box>

            {/* Bulk assign modal */}
            <BulkAssignModal
                open={bulkAssignOpen}
                onClose={() => setBulkAssignOpen(false)}
                practiceId={practiceId}
                activeTasks={activeTasks}
                onSaved={() => { setBulkAssignOpen(false); refresh(); }}
            />
        </Box>
    );
}

AttendedGuidedChecklist.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadDetail: PropTypes.object,
    /** 'gate-first' = popup 1 (BOOKED → ATTENDED). 'post-attended' = popup 2. */
    mode: PropTypes.oneOf(['gate-first', 'post-attended']).isRequired,
    /** Called by the all-done card's Close button. */
    handleClose: PropTypes.func,
    /** Called when user clicks "No" in the gate — parent opens FTA popup. */
    handleNoAttended: PropTypes.func,
    /**
     * When true, the gate is rendered by the host component (AttendedAppointment
     * hero card) instead of internally. The orchestrator skips its own gate render
     * and watches externalGateDismissed instead of its own state.
     */
    gateRenderedInHost: PropTypes.bool,
    /** External gate-dismissed flag — used when gateRenderedInHost=true. */
    externalGateDismissed: PropTypes.bool,
    // handleCloseAndReload accepted by callers; will be declared in Phase 4+.
    /** Deferred table reload — called ~3s after archive dispatched so Celery drains first. */
    handleReloadLeads: PropTypes.func,
    /** Fires after a task completes, sends, or skips server-side. Host pops Finish open. */
    onTaskActivity: PropTypes.func,
    /** When provided, the all-done / no-tasks card shows a "Create a custom reminder" CTA
     *  instead of the legacy Close button. Pass () => setShowCustomReminder(true) from the host. */
    onCreateReminder: PropTypes.func,
};

AttendedGuidedChecklist.defaultProps = {
    leadDetail: null,
    onTaskActivity: null,
    handleClose: null,
    handleNoAttended: null,
    gateRenderedInHost: false,
    externalGateDismissed: false,
    onCreateReminder: null,
};
